import PropTypes from "prop-types";
import Drawer from "@mui/material/Drawer";
import {
  SIDE_DRAWER_UI,
  sideDrawerBackdropSx,
  sideDrawerPaperSx,
  sideDrawerRootSx,
} from "./SideDrawer.constants";
import {
  CancelButton,
  DrawerBody,
  DrawerCloseButton,
  DrawerCloseIcon,
  DrawerDescription,
  DrawerFooter,
  DrawerHeaderStacked,
  DrawerHeaderText,
  DrawerShell,
  DrawerTitle,
  SubmitButton,
} from "./SideDrawer.styles";

const SideDrawerLayout = ({
  open,
  onClose,
  title,
  titleId,
  description,
  children,
  onSubmit,
  submitLabel,
  cancelLabel,
  submitting,
  submitDisabled,
  cancelDisabled,
  submitFormId,
  submitTestId,
  testId,
  paperSx,
  showFooter,
}) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    sx={sideDrawerRootSx}
    ModalProps={{
      BackdropProps: { sx: sideDrawerBackdropSx },
    }}
    PaperProps={{ sx: { ...sideDrawerPaperSx, ...paperSx } }}
    data-testid={testId}
  >
    <DrawerShell role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <DrawerHeaderStacked>
        <DrawerHeaderText>
          <DrawerTitle id={titleId} component="h2">
            {title}
          </DrawerTitle>
          {description ? (
            <DrawerDescription component="p">{description}</DrawerDescription>
          ) : null}
        </DrawerHeaderText>
        <DrawerCloseButton
          type="button"
          aria-label={SIDE_DRAWER_UI.closeAriaLabel}
          title={SIDE_DRAWER_UI.closeAriaLabel}
          onClick={onClose}
        >
          <DrawerCloseIcon />
        </DrawerCloseButton>
      </DrawerHeaderStacked>

      <DrawerBody>{children}</DrawerBody>

      {showFooter ? (
        <DrawerFooter>
          <CancelButton
            type="button"
            onClick={onClose}
            disabled={cancelDisabled}
          >
            {cancelLabel}
          </CancelButton>
          <SubmitButton
            type={submitFormId ? "submit" : "button"}
            form={submitFormId}
            variant="contained"
            disabled={submitting || submitDisabled}
            onClick={onSubmit}
            data-testid={submitTestId}
          >
            {submitLabel}
          </SubmitButton>
        </DrawerFooter>
      ) : null}
    </DrawerShell>
  </Drawer>
);

SideDrawerLayout.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  titleId: PropTypes.string,
  description: PropTypes.node,
  children: PropTypes.node,
  onSubmit: PropTypes.func,
  submitLabel: PropTypes.node,
  cancelLabel: PropTypes.node,
  submitting: PropTypes.bool,
  submitDisabled: PropTypes.bool,
  cancelDisabled: PropTypes.bool,
  /** Id of a form rendered in the body; lets the footer button submit it. */
  submitFormId: PropTypes.string,
  submitTestId: PropTypes.string,
  testId: PropTypes.string,
  paperSx: PropTypes.object,
  showFooter: PropTypes.bool,
};

SideDrawerLayout.defaultProps = {
  titleId: "side-drawer-layout-title",
  description: undefined,
  children: null,
  onSubmit: undefined,
  submitLabel: SIDE_DRAWER_UI.submitLabel,
  cancelLabel: SIDE_DRAWER_UI.cancelLabel,
  submitting: false,
  submitDisabled: false,
  cancelDisabled: false,
  submitFormId: undefined,
  submitTestId: undefined,
  testId: undefined,
  paperSx: undefined,
  showFooter: true,
};

export default SideDrawerLayout;
